import { z } from 'zod';
import { stateKeySchema } from '../catalogue/state-rules.js';
import { batchDestinationSchema } from '../production/batches.js';
import { compareQty, nonNegativeQtyString, positiveQtyString, sumQty } from '../quantity/index.js';
import { defineCommand } from './registry.js';
import { docNumberSchema } from './stock.js';

/**
 * Production queue commands (M5, plan §9.1, §9.5): start a batch, complete it (a physical
 * fact: components consumed FEFO, the output lot made, exactly once), cancel it.
 * @module
 */

const instant = z.iso.datetime({ offset: true });

/**
 * A batch a station makes on the spot (not planned in the Admin): the station makes the id
 * and the `PB-<device>-<seq>` number, and pins the recipe version it has.
 */
export const newBatchSchema = z.object({
  number: docNumberSchema('PB'),
  product_id: z.uuid(),
  recipe_version_id: z.uuid(),
  /** In the product's base unit. */
  planned_qty: positiveQtyString,
  planned_start: instant.optional(),
});

/**
 * `batch.start` (non-physical, `production.run`): the baker started a batch. Recorded once
 * (`batch_state_transition` (batch, start) is unique): starting a started or completed batch
 * again is a no-op (`data.already`); starting a cancelled one is rejected. `new_batch`
 * creates an unplanned batch first.
 */
export const batchStart = defineCommand({
  type: 'batch.start',
  versions: { current: 1 },
  payload: z.object({
    batch_id: z.uuid(),
    started_at: instant,
    new_batch: newBatchSchema.optional(),
  }),
  physical: false,
  permission: 'production.run',
});

/** Where part of the output went (vault "destination-tagged production"). */
export const batchAllocationInputSchema = z.object({
  destination: batchDestinationSchema,
  /** In the product's base unit. */
  qty: positiveQtyString,
  /** Where the stock is put; default: the branch's shelf (sellable) or production location. */
  location_id: z.uuid().optional(),
  /** The order line the output is claimed for (`order_claim`, `immediate_order`). */
  order_line_id: z.uuid().optional(),
  /** The branch it goes to (`branch_transfer`: refused until transfers exist, CR8A-6). */
  to_branch_id: z.uuid().optional(),
});
/** One allocation of a batch's output. */
export type BatchAllocationInput = z.infer<typeof batchAllocationInputSchema>;

/** A component lot the baker scanned: it replaces FEFO for that component. */
export const scannedComponentLotSchema = z.object({
  product_id: z.uuid(),
  lot_id: z.uuid(),
  location_id: z.uuid(),
  state: stateKeySchema.optional(),
  /**
   * In the component's base unit. Default: what the other scans' quantities leave of the
   * recipe's need, spread over the scans without one.
   */
  qty: positiveQtyString.optional(),
});

/**
 * `batch.complete` (physical, `production.complete`): a batch came out of the oven.
 * Exactly once per batch (`batch_state_transition` (batch, complete) is unique): the first
 * completion consumes the components (made-ahead products; FEFO from production-feeding
 * locations, or the scanned lots; beyond stock with a flag, never rejected), makes the
 * output lot (the station's id and `L-…` code; best-before from the product's shelf life,
 * local date) at the allocations' locations and records the actual yield. A second
 * completion (another tablet, a resend under a new id) changes nothing and answers with the
 * first completion's result, flagged `already_completed`.
 */
export const batchComplete = defineCommand({
  type: 'batch.complete',
  versions: { current: 1 },
  payload: z
    .object({
      batch_id: z.uuid(),
      completed_at: instant,
      /** In the product's base unit (0 = nothing usable came out). */
      actual_yield_qty: nonNegativeQtyString,
      /** What the recipe was scaled for (default: the planned quantity). */
      input_qty: positiveQtyString.optional(),
      /** The output lot, made on the station (so its label prints offline). */
      output_lot: z.object({ lot_id: z.uuid(), lot_code: docNumberSchema('L') }),
      /** Split of the actual yield; the rest goes to the default location. */
      allocations: z.array(batchAllocationInputSchema).max(50).optional(),
      component_lots: z.array(scannedComponentLotSchema).max(200).optional(),
      /** The recipe version the station worked from (recorded; plan §9.5). */
      recipe_version_id: z.uuid().optional(),
      note: z.string().trim().max(500).optional(),
      /**
       * A batch the station made on the spot (as on its `batch.start`). If the server has no
       * such batch — its start was refused, or never arrived — the completion creates it,
       * flagged `created_by_completion`: the goods exist either way (D-065).
       */
      new_batch: newBatchSchema.optional(),
    })
    .superRefine((p, ctx) => {
      const total = sumQty((p.allocations ?? []).map((a) => a.qty));
      if (compareQty(total, p.actual_yield_qty) > 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Allocations add up to more than the actual yield',
          path: ['allocations'],
        });
      }
      (p.allocations ?? []).forEach((a, i) => {
        // Transfers between branches aren't built in V1 (one branch; review CR8A-6): the
        // stock would stay at this branch while the plan counted it gone. Refused until they
        // are; the station never offers it, so only a wrong client lands here (and the
        // server holds it for a person, D-024).
        if (a.destination === 'branch_transfer') {
          ctx.addIssue({
            code: 'custom',
            message: 'Transfers to another branch aren’t available yet: put it on the shelf',
            path: ['allocations', i, 'destination'],
          });
        }
      });
    }),
  physical: true,
  permission: 'production.complete',
});

/**
 * `batch.cancel` (non-physical, `production.plan`): a planned or started batch won't be
 * made. Nothing was consumed (consumption happens at completion). A completed batch can't
 * be cancelled (rejected `already_completed`).
 */
export const batchCancel = defineCommand({
  type: 'batch.cancel',
  versions: { current: 1 },
  payload: z.object({
    batch_id: z.uuid(),
    reason: z.string().trim().min(1).max(500),
  }),
  physical: false,
  permission: 'production.plan',
});

/** The production commands (M5). */
export const PRODUCTION_COMMANDS = [batchStart, batchComplete, batchCancel] as const;
